/**
 * useBuddyBackend — fetches Buddy's current state and recent events from the backend.
 * This is the SOLE source of truth for Buddy's state. Frontend is read-only.
 *
 * Optimizations:
 * - ETag / If-None-Match → skips JSON parse on 304
 * - Visibility-aware polling: 30s foreground, 180s hidden, refresh on return
 * - Stable state reference to prevent unnecessary re-renders
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { BuddyEvent } from '@/lib/types/events';
import { LongTermMoodScores } from '@/lib/types/mood';
import { EnvironmentState } from '@/lib/state/environmentMoodComposer';
import { BuddyMacroState } from '@/lib/state/macroAdvisorEngine';

/** Shallow compare key scalar fields that drive UI re-renders */
function stateEqual(a: BackendBuddyState | null, b: BackendBuddyState | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.buddyMood === b.buddyMood
    && a.networkState === b.networkState
    && a.blockState === b.blockState
    && a.priceState === b.priceState
    && a.weather === b.weather
    && a.heatLevel === b.heatLevel
    && a.verdict === b.verdict
    && a.dayArchetype === b.dayArchetype
    && a.dailySummary === b.dailySummary
    && a.weeklyVibe === b.weeklyVibe
    && a.macroMode === b.macroMode
    && a.sentimentBucket === b.sentimentBucket
    && a.sentimentValue === b.sentimentValue
    && a.activeSignature === b.activeSignature
    && a.seasonState === b.seasonState
    && a.isStale === b.isStale
    && a.updatedAt === b.updatedAt
    && a.moodLabel === b.moodLabel
    && a.plainSubtitle === b.plainSubtitle
    && a.macroExplanation === b.macroExplanation
    && a.whatItMeans === b.whatItMeans
    && a.footerLine === b.footerLine
    && a.displayMacroState === b.displayMacroState;
}

export interface NewsItem {
  title: string;
  source: string;
  url: string | null;
  pubDate: number;
}

export interface BackendBuddyState {
  // Short-term
  networkState: string;
  blockState: string;
  priceState: string;
  buddyMood: string;
  // Weather
  weather: string;
  weatherDescription: string;
  // Heat
  heatLevel: number;
  // Copy layers
  verdict: string;
  dayArchetype: string;
  dailySummary: string;
  weeklyVibe: string;
  // Macro/sentiment
  macroMode: string;
  sentimentValue: number | null;
  sentimentBucket: string | null;
  // Long-term
  longTermScores: LongTermMoodScores;
  networkMood: string;
  marketMood: string;
  blockMood: string;
  ltConfidence: string;
  ltStatusLabel: string;
  // Environment (ambient visuals)
  environment: EnvironmentState;
  // Signature
  activeSignature: string | null;
  // Season (50WMA)
  seasonState: string;
  seasonDeviationPct: number;
  seasonIntensity: number;
  // Macro advisor (full scoring from backend)
  macroAdvisor: BuddyMacroState | null;
  // New macro display copy
  moodLabel: string | null;
  plainSubtitle: string | null;
  macroExplanation: string | null;
  whatItMeans: string | null;
  footerLine: string | null;
  displayMacroState: string | null;
  // News
  newsItems: NewsItem[];
  // Meta
  updatedAt: string;
  isStale: boolean;
}

export interface BackendMeta {
  snapshotCount: number;
  workerStatus: string;
  staleSec: number;
  cold: boolean;
}

export interface BuddyBackendOutput {
  state: BackendBuddyState | null;
  events: BuddyEvent[];
  meta: BackendMeta;
  loading: boolean;
  error: boolean;
  lastFetch: number;
}

const DEFAULT_META: BackendMeta = {
  snapshotCount: 0,
  workerStatus: 'unknown',
  staleSec: -1,
  cold: true,
};

const DEFAULT_ENVIRONMENT: EnvironmentState = {
  particleDensity: 0.15,
  particleSpeed: 0.2,
  particleOpacity: 0.25,
  ambientBrightness: 0.5,
  ambientWarmth: 0.5,
  glowIntensity: 0.15,
  spaciousness: 0.8,
  tensionLevel: 0.05,
  stillness: 0.8,
  bgGradient: 'radial-gradient(ellipse at 50% 50%, hsl(30 95% 56% / 0.02) 0%, transparent 65%)',
  pulseFrequency: 6,
};

const DEFAULT_SCORES: LongTermMoodScores = {
  sustained_calm: 0.5,
  sustained_congestion: 0,
  sustained_bullish: 0,
  sustained_caution: 0,
  sustained_crab: 0.3,
  sustained_block_pressure: 0,
  sustained_smooth_flow: 0.2,
};

// ── Polling intervals ──
const POLL_FOREGROUND = 30_000;   // 30s when visible
const POLL_BACKGROUND = 180_000;  // 3min when hidden tab

export function useBuddyBackend(): BuddyBackendOutput {
  const [state, setState] = useState<BackendBuddyState | null>(null);
  const [events, setEvents] = useState<BuddyEvent[]>([]);
  const [meta, setMeta] = useState<BackendMeta>(DEFAULT_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lastFetch, setLastFetch] = useState(0);
  const mountedRef = useRef(true);
  const etagRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchState = useCallback(async () => {
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID ?? 'kpomlqozspvmleljtcxp';
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const url = `https://${projectId}.supabase.co/functions/v1/buddy-state?events=5`;

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${anonKey}`,
        'Content-Type': 'application/json',
      };

      // Send ETag for conditional request
      if (etagRef.current) {
        headers['If-None-Match'] = etagRef.current;
      }

      const res = await fetch(url, { headers });

      if (!mountedRef.current) return;

      // 304 Not Modified — state unchanged, skip processing
      if (res.status === 304) {
        setError(false);
        setLastFetch(Date.now());
        if (loading) setLoading(false);
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Store new ETag
      const newETag = res.headers.get('etag');
      if (newETag) etagRef.current = newETag;

      const data = await res.json();

      if (!mountedRef.current) return;

      if (!data?.ok) {
        setError(true);
        return;
      }

      const s = data.state;

      // Parse environment from JSON, with safe fallback
      let env = DEFAULT_ENVIRONMENT;
      if (s.environment && typeof s.environment === 'object') {
        env = {
          particleDensity: s.environment.particleDensity ?? DEFAULT_ENVIRONMENT.particleDensity,
          particleSpeed: s.environment.particleSpeed ?? DEFAULT_ENVIRONMENT.particleSpeed,
          particleOpacity: s.environment.particleOpacity ?? DEFAULT_ENVIRONMENT.particleOpacity,
          ambientBrightness: s.environment.ambientBrightness ?? DEFAULT_ENVIRONMENT.ambientBrightness,
          ambientWarmth: s.environment.ambientWarmth ?? DEFAULT_ENVIRONMENT.ambientWarmth,
          glowIntensity: s.environment.glowIntensity ?? DEFAULT_ENVIRONMENT.glowIntensity,
          spaciousness: s.environment.spaciousness ?? DEFAULT_ENVIRONMENT.spaciousness,
          tensionLevel: s.environment.tensionLevel ?? DEFAULT_ENVIRONMENT.tensionLevel,
          stillness: s.environment.stillness ?? DEFAULT_ENVIRONMENT.stillness,
          bgGradient: s.environment.bgGradient ?? DEFAULT_ENVIRONMENT.bgGradient,
          pulseFrequency: s.environment.pulseFrequency ?? DEFAULT_ENVIRONMENT.pulseFrequency,
        };
      }

      // Parse long-term scores
      let scores = DEFAULT_SCORES;
      if (s.longTermScores && typeof s.longTermScores === 'object') {
        scores = {
          sustained_calm: s.longTermScores.sustained_calm ?? 0,
          sustained_congestion: s.longTermScores.sustained_congestion ?? 0,
          sustained_bullish: s.longTermScores.sustained_bullish ?? 0,
          sustained_caution: s.longTermScores.sustained_caution ?? 0,
          sustained_crab: s.longTermScores.sustained_crab ?? 0,
          sustained_block_pressure: s.longTermScores.sustained_block_pressure ?? 0,
          sustained_smooth_flow: s.longTermScores.sustained_smooth_flow ?? 0,
        };
      }

      const newState: BackendBuddyState = {
        networkState: s.networkState,
        blockState: s.blockState,
        priceState: s.priceState,
        buddyMood: s.buddyMood,
        weather: s.weather,
        weatherDescription: s.weatherDescription,
        heatLevel: Number(s.heatLevel),
        verdict: s.verdict,
        dayArchetype: s.dayArchetype,
        dailySummary: s.dailySummary,
        weeklyVibe: s.weeklyVibe,
        macroMode: s.macroMode,
        sentimentValue: s.sentimentValue,
        sentimentBucket: s.sentimentBucket,
        longTermScores: scores,
        networkMood: s.networkMood ?? 'SUSTAINED_NEUTRAL',
        marketMood: s.marketMood ?? 'SUSTAINED_NEUTRAL',
        blockMood: s.blockMood ?? 'SUSTAINED_NEUTRAL',
        ltConfidence: s.ltConfidence,
        ltStatusLabel: s.ltStatusLabel,
        environment: env,
        activeSignature: s.activeSignature,
        seasonState: s.seasonState ?? 'neutral',
        seasonDeviationPct: Number(s.seasonDeviationPct ?? 0),
        seasonIntensity: Number(s.seasonIntensity ?? 0),
        macroAdvisor: s.macroAdvisor ?? null,
        moodLabel: s.moodLabel ?? null,
        plainSubtitle: s.plainSubtitle ?? null,
        macroExplanation: s.macroExplanation ?? null,
        whatItMeans: s.whatItMeans ?? null,
        footerLine: s.footerLine ?? null,
        displayMacroState: s.displayMacroState ?? null,
        newsItems: Array.isArray(s.newsItems) ? s.newsItems : [],
        updatedAt: s.updatedAt,
        isStale: s.isStale,
      };

      // Only update state reference if data actually changed — prevents re-renders & animation restarts
      setState(prev => stateEqual(prev, newState) ? prev : newState);

      setEvents(data.events ?? []);
      setMeta(data.meta ?? DEFAULT_META);
      setError(false);
      setLastFetch(Date.now());
    } catch {
      if (mountedRef.current) setError(true);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // ── Visibility-aware polling ──
  useEffect(() => {
    mountedRef.current = true;
    fetchState(); // initial fetch

    const startPolling = (interval: number) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(fetchState, interval);
    };

    // Start with foreground interval
    startPolling(document.hidden ? POLL_BACKGROUND : POLL_FOREGROUND);

    const handleVisibility = () => {
      if (document.hidden) {
        // Tab hidden → slow poll
        startPolling(POLL_BACKGROUND);
      } else {
        // Tab visible → immediate refresh + fast poll
        fetchState();
        startPolling(POLL_FOREGROUND);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchState]);

  return { state, events, meta, loading, error, lastFetch };
}
